import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../hooks/useNotification'
import { NOTIFICATION_TYPES } from '../../constants/notification'
import { BellIcon } from '@heroicons/react/24/outline'

export const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const navigate = useNavigate()
  const { userType } = useAuth()
  const { data, getTotalNotificationCount, getNotificationCount, resetNotification } =
    useNotification()

  const totalNotifications = getTotalNotificationCount()

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleNotificationClick = async (notificationType) => {
    const routes = NOTIFICATION_TYPES[notificationType].routes
    if (!routes) {
      console.warn(`No routes defined for notification type: ${notificationType}`)
      return
    }

    const route = routes[userType] || routes.customer // Fallback to customer route
    if (!route) {
      console.warn(`No route found for user type: ${userType}, notification: ${notificationType}`)
      return
    }

    await resetNotification(notificationType)
    navigate(route)
    setIsOpen(false)
  }

  const getActiveNotifications = () => {
    if (!data) return []

    return Object.values(NOTIFICATION_TYPES)
      .filter(({ key }) => getNotificationCount(key) > 0)
      .map(({ key, label }) => ({
        type: key,
        count: getNotificationCount(key),
        label,
      }))
  }

  const activeNotifications = getActiveNotifications()

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-neutral-200-100 rounded-md transition-colors"
        aria-label="Notifications"
      >
        <BellIcon className="w-5 h-5 sm:w-6 sm:h-6 text-icon-default" />
        {totalNotifications > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center min-w-4">
            {totalNotifications > 99 ? '99+' : totalNotifications}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-background rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="py-2">
            <div className="px-4 py-2 text-sm font-medium text-gray-900 border-b border-gray-200">
              Notifications ({totalNotifications})
            </div>

            {activeNotifications.length > 0 ? (
              <div className="max-h-96 overflow-y-auto">
                {activeNotifications.map(({ type, count, label }) => {
                  return (
                    <button
                      key={type}
                      onClick={() => handleNotificationClick(type)}
                      className="w-full text-left px-4 py-3 hover:bg-neutral-200-50 transition-colors border-b border-gray-100 last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{label}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {`${count} new ${count === 1 ? 'item' : 'items'}`}
                          </p>
                        </div>
                        <div className="ml-3">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {count}
                          </span>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            ) : (
              <div className="px-4 py-6 text-center">
                <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">No new notifications</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationDropdown
